import { Migration } from '@gleamer/types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useMigrationGroups,
  useMigrationRuns,
  useMigrationSources,
} from '../../../../hooks/useMigration';
import { PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import { Card, Field, Pager } from '../../../atoms/Surface';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import { ProgressBar } from '../imports/ProgressBar';
import {
  CatchUpPhaseBadge,
  CommandButton,
  formatCount,
  RunStateBadge,
} from './parts';
import { selectClassName } from '../../../atoms/Surface';
import { DATE_LOCALE } from '../../../../utils/locale';

type MigrationRun = Migration.MigrationRun;
type MigrationRunState = Migration.MigrationRunState;
type MigrationBigQueryDatasets = Migration.MigrationBigQueryDatasets;

const { MIGRATION_RUN_STATES } = Migration;

/** A radio or a checkbox shown as a pill: the input stays, hidden, for the keyboard and screen readers. */
function Choice({
  type,
  checked,
  onChange,
  name,
  value,
  disabled = false,
  children,
}: React.PropsWithChildren<{
  type: 'radio' | 'checkbox';
  checked: boolean;
  onChange: () => void;
  name?: string;
  value?: string;
  disabled?: boolean;
}>) {
  return (
    <label
      className={classNames(
        'rounded-full px-4 py-1.5 text-base ring-1 transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-dl-primary',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        checked
          ? 'bg-dl-primary text-white ring-dl-primary'
          : classNames('bg-dl-surface text-dl-text ring-dl-border', !disabled && 'hover:bg-dl-tint')
      )}
    >
      <input
        type={type}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
      />
      {children}
    </label>
  );
}

export const MIGRATION_RUNS_PAGE_SIZE = 20;

/** How long a started run is waited for before the Start button is given back anyway. */
export const START_WAIT_MS = 60_000;

/** The value of a URL parameter when it is one of `values`, else nothing — a hand-edited URL is not trusted. */
function oneOf<T extends string>(
  value: string | null,
  values: readonly T[]
): T | undefined {
  return values.find(v => v === value);
}

/**
 * Starts a bulk copy from a g3d environment — test, QA or prod on dev and testing, prod alone on prod: of every group
 * or of some (each needs the ones before it copied). From a source whose events are followed, the run has a catch-up:
 * it piles g3d's changes while the run copies, applies them once it is done, and replaces the catch-up that followed
 * before; it is stopped at the freeze. With several sources, one must be picked: copying from g3d prod is never a
 * default.
 */
function StartRun({
  awaiting,
  onStarted,
}: {
  awaiting: boolean;
  onStarted: (requestId: string) => void;
}) {
  const groups = useMigrationGroups();
  const sources = useMigrationSources();
  const [chosen, setChosen] = useState<string[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  // the source's BigQuery datasets unticked: every other one is copied
  const [leftOut, setLeftOut] = useState<string[]>([]);

  const offered = sources.data ?? [];
  const source =
    offered.length === 1 ? offered[0] : offered.find(s => s.key === picked);
  const from = source?.label ?? 'the source';
  // BigQuery goes with a bulk run over every group: the rewrite looks rows up in all of them
  const copiesBigQuery = chosen.length === 0;
  const bigquery = source?.bigquery ?? null;
  const excluded = copiesBigQuery
    ? leftOut.filter(name => bigquery?.datasets.some(d => d.name === name))
    : [];

  const toggle = (group: string) =>
    setChosen(current =>
      current.includes(group)
        ? current.filter(g => g !== group)
        : [...current, group]
    );

  return (
    <Card
      id="start-run-title"
      title="Start a run"
      className="mb-6"
      description={`Copies ${from} into the platform, group by group, each after the ones it needs.`}
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-dl-muted">Source</legend>
        {sources.status === 'error' && (
          <p role="alert" className="text-dl-danger">
            Could not load the sources: {(sources.error as Error)?.message}
          </p>
        )}
        {sources.status === 'success' && offered.length === 0 && (
          <p className="text-base text-dl-muted">
            migration-service has published no source: is it running?
          </p>
        )}
        {offered.length === 1 && (
          <p className="text-base text-dl-text">{offered[0].label}</p>
        )}
        {offered.length > 1 && (
          <div role="radiogroup" aria-label="Source" className="flex flex-wrap gap-2">
            {offered.map(s => (
              <Choice
                key={s.key}
                type="radio"
                name="run-source"
                value={s.key}
                checked={picked === s.key}
                onChange={() => {
                  setPicked(s.key);
                  setLeftOut([]);
                }}
              >
                {s.label}
              </Choice>
            ))}
          </div>
        )}
        {source && (
          <p className="mt-2 text-sm text-dl-muted">
            {source.catchUp
              ? `Its catch-up follows ${source.label}’s changes; any catch-up following now stops.`
              : `${source.label}’s changes are not followed: no catch-up.`}
          </p>
        )}
      </fieldset>
      <fieldset className="mt-4">
        <legend className="mb-2 text-sm font-medium text-dl-muted">
          Groups — none chosen copies them all, in order
        </legend>
        <div className="flex flex-wrap gap-2">
          {(groups.data ?? []).map(group => (
            <Choice
              key={group}
              type="checkbox"
              checked={chosen.includes(group)}
              onChange={() => toggle(group)}
            >
              {group}
            </Choice>
          ))}
        </div>
      </fieldset>
      {bigquery && (
        <BigQueryDatasets
          bigquery={bigquery}
          from={from}
          copied={copiesBigQuery}
          leftOut={leftOut}
          onToggle={name =>
            setLeftOut(current =>
              current.includes(name) ? current.filter(n => n !== name) : [...current, name]
            )
          }
        />
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <CommandButton
          label="Start the bulk copy"
          confirm={`Copy ${chosen.length ? chosen.join(', ') : 'every group'} from ${from}${
            excluded.length ? `, without the BigQuery datasets ${excluded.join(', ')}` : ''
          }?`}
          command={{
            type: 'START',
            runType: 'BULK',
            groups: chosen,
            source: source?.key,
            bigqueryExcluded: excluded,
          }}
          disabled={awaiting || !source}
          onSent={requested => {
            setChosen([]);
            setLeftOut([]);
            onStarted(requested.requestId);
          }}
        />
        {awaiting && (
          <span className="text-base text-dl-muted">
            Waiting for migration-service to record the run…
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * The source's BigQuery datasets, every one ticked: a bulk run over every group copies them as g3d has them, but those
 * unticked. Only listed — migration-service lists them at its startup and at each run's start, so one created since
 * is copied all the same.
 */
function BigQueryDatasets({
  bigquery,
  from,
  copied,
  leftOut,
  onToggle,
}: {
  bigquery: MigrationBigQueryDatasets;
  from: string;
  copied: boolean;
  leftOut: string[];
  onToggle: (name: string) => void;
}) {
  return (
    <fieldset className="mt-4">
      <legend className="mb-2 text-sm font-medium text-dl-muted">
        BigQuery datasets — every one is copied, as {from} has them; untick one to leave it out
      </legend>
      {bigquery.error && (
        <p
          role="alert"
          className="mb-2 text-dl-danger"
        >
          migration-service could not list {from}’s datasets: {bigquery.error}. A run over every group
          lists them again when it starts, and is not recorded while that fails.
        </p>
      )}
      {!copied && (
        <p className="mb-2 text-base text-dl-muted">
          Only a run over every group copies BigQuery: its export tables are rewritten to the ids of
          all the others.
        </p>
      )}
      {bigquery.datasets.length === 0 && !bigquery.error && (
        <p className="text-base text-dl-muted">{from} has no dataset.</p>
      )}
      <div className="flex flex-wrap gap-2">
        {bigquery.datasets.map(dataset => (
          <Choice
            key={dataset.name}
            type="checkbox"
            checked={copied && !leftOut.includes(dataset.name)}
            disabled={!copied}
            onChange={() => onToggle(dataset.name)}
          >
            {dataset.name}
            {dataset.location && (
              <span className="ml-1.5 text-sm opacity-70">{dataset.location}</span>
            )}
          </Choice>
        ))}
      </div>
      {bigquery.listedAt && (
        <p className="mt-2 text-sm text-dl-muted">Listed {formatDateTime(bigquery.listedAt)}</p>
      )}
    </fieldset>
  );
}

/** A run's catch-up in a line: its phase, its backlog, and the conflicts someone has to look at. */
function CatchUpCell({ run }: { run: MigrationRun }) {
  const catchUp = run.catchUp;
  if (!catchUp) {
    return <span className="text-dl-faint">—</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1 whitespace-nowrap text-sm">
      <CatchUpPhaseBadge phase={catchUp.phase} />
      {catchUp.pending > 0 && <span>{formatCount(catchUp.pending)} pending</span>}
      {catchUp.conflicts > 0 && (
        <span className="font-medium text-dl-warning">
          {formatCount(catchUp.conflicts)} conflict{catchUp.conflicts === 1 ? '' : 's'}
        </span>
      )}
    </span>
  );
}

function RunProgress({ run }: { run: MigrationRun }) {
  const s = run.segments;
  return (
    <div className="min-w-[10rem]">
      <ProgressBar
        label={`Run ${run.id} segments`}
        segments={[
          { label: 'Done', value: s.done, className: 'bg-dl-success' },
          { label: 'Failed', value: s.failed, className: 'bg-dl-danger' },
          { label: 'Running', value: s.running, className: 'bg-dl-primary' },
          { label: 'Pending', value: s.pending, className: 'bg-dl-faint' },
        ]}
      />
      <span className="whitespace-nowrap text-sm text-dl-muted">
        {s.done} / {s.total} segments
        {s.failed > 0 && (
          <span className="ml-1 text-dl-danger">({s.failed} failed)</span>
        )}
      </span>
    </div>
  );
}

export function MigrationRunsList({
  awaiting = false,
}: {
  awaiting?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = oneOf<MigrationRunState>(
    searchParams.get('state'),
    MIGRATION_RUN_STATES
  );
  const page = Math.max(0, parseInt(searchParams.get('page') ?? '0', 10) || 0);
  const set = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key)
    );
    setSearchParams(next);
  };

  const { data, status, error, refetch, isFetching, dataUpdatedAt } =
    useMigrationRuns(
      {
        page,
        size: MIGRATION_RUNS_PAGE_SIZE,
        state,
      },
      awaiting
    );

  return (
    <Card
      id="migration-runs-title"
      title="Runs"
      description={dataUpdatedAt > 0 && `Updated at ${new Date(dataUpdatedAt).toLocaleTimeString(DATE_LOCALE)}.`}
      actions={
        <>
          <Field label="State">
            <select
              className={selectClassName}
              value={state ?? ''}
              onChange={e => set({ state: e.target.value || undefined, page: undefined })}
            >
              <option value="">All</option>
              {MIGRATION_RUN_STATES.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <PageButton onClick={() => refetch()} disabled={isFetching} className="mx-0 h-10 px-5">
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </PageButton>
        </>
      }
    >
      {status === 'loading' && <div className="text-dl-muted">Loading…</div>}
      {status === 'error' && (
        <div role="alert" className="text-dl-danger">
          Could not load the migration runs: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && data.items.length === 0 && (
        <div className="py-8 text-center text-lg text-dl-muted">No migration run.</div>
      )}
      {status === 'success' && data.items.length > 0 && (
        <>
          <div className="-mx-6 overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <TableHeadTh className="pl-6">Run</TableHeadTh>
                  <TableHeadTh>Source</TableHeadTh>
                  <TableHeadTh>State</TableHeadTh>
                  <TableHeadTh>Groups</TableHeadTh>
                  <TableHeadTh>Started</TableHeadTh>
                  <TableHeadTh>Progress</TableHeadTh>
                  <TableHeadTh>Catch-up</TableHeadTh>
                  <TableHeadTh className="pr-6 text-right">Errors</TableHeadTh>
                </tr>
              </thead>
              <tbody>
                {data.items.map(run => (
                  <tr key={run.id} className="group">
                    <TableBodyTd className="pl-6">
                      <Link
                        className="font-medium text-dl-accent hover:underline"
                        to={`/backoffice/migration/${run.id}`}
                        state={{ listSearch: searchParams.toString() }}
                      >
                        #{run.id}
                      </Link>
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap" title={run.requestedBy ?? undefined}>
                      {run.sourceLabel ?? run.source}
                    </TableBodyTd>
                    <TableBodyTd>
                      <RunStateBadge state={run.state} />
                    </TableBodyTd>
                    <TableBodyTd className="max-w-xs text-sm text-dl-muted">
                      {run.groups.join(', ')}
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap">
                      {formatDateTime(run.startedAt ?? run.requestedAt)}
                    </TableBodyTd>
                    <TableBodyTd>
                      <RunProgress run={run} />
                    </TableBodyTd>
                    <TableBodyTd>
                      <CatchUpCell run={run} />
                    </TableBodyTd>
                    <TableBodyTd
                      className={classNames(
                        'pr-6 text-right tabular-nums',
                        run.unacknowledgedErrors > 0 && 'font-medium text-dl-danger'
                      )}
                    >
                      {formatCount(run.unacknowledgedErrors)}
                    </TableBodyTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={data.page}
            pages={data.pages}
            total={data.total}
            noun="runs"
            onPage={next => set({ page: next > 0 ? String(next) : undefined })}
          />
        </>
      )}
    </Card>
  );
}

/**
 * The data migration from g3d (DATAT-603): its runs, and starting one. A START is applied by migration-service
 * a moment after viewer-api accepts it, so the Start button stays off until the run it asked for is listed
 * (or START_WAIT_MS passed): a second click meanwhile would ask for a second run.
 */
export function MigrationPage() {
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const recent = useMigrationRuns({ page: 0, size: 5 }, awaiting !== null);

  useEffect(() => {
    if (
      awaiting &&
      recent.data?.items.some(run => run.requestId === awaiting)
    ) {
      setAwaiting(null);
    }
  }, [awaiting, recent.data]);
  useEffect(() => {
    if (!awaiting) {
      return;
    }
    const timer = setTimeout(() => setAwaiting(null), START_WAIT_MS);
    return () => clearTimeout(timer);
  }, [awaiting]);

  return (
    <div>
      <PageTitle title="Data migration" />
      <StartRun awaiting={awaiting !== null} onStarted={setAwaiting} />
      <MigrationRunsList awaiting={awaiting !== null} />
    </div>
  );
}

export default MigrationPage;
